import React, { useState, useEffect, useRef } from 'react';

// Libs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNodeId } from '@xyflow/react';

// Funções
import { useNodeUpdate } from '~/hooks/useNodeUpdate';

// Utilitários
import { NodeHeaderCustomDeleteAction } from '~/utils/NodeHeaderCustomDeleteAction';
import { NodeHeaderCustomAlertAction } from '~/utils/NodeHeaderCustomAlertAction';
import { CustomHandle } from '~/utils/CustomHandle';
import { importFromGeniaWeb, validateImportedData } from "~/utils/fileUtils";

// Node de Fluxograma
// Este componente permite que o usuário execute outro fluxograma dentro do fluxo atual
// O usuário pode selecionar um arquivo .geniaweb para ser executado quando o fluxo chegar neste nó
// O fluxograma importado será executado na ordem dos seus nós e depois continua o fluxo principal
export const FluxogramaNode = ({ data }) => {
    const id = useNodeId();
    const { onNodesChange } = useNodeUpdate();
    const fileInputRef = useRef(null);
    
    const [nomeArquivo, setNomeArquivo] = useState(data.nomeArquivo || '');
    const [fluxogramaData, setFluxogramaData] = useState(data.fluxogramaData || null);
    const [status, setStatus] = useState(data.status || 'aguardando'); // 'aguardando', 'carregado', 'erro'
    const [listaPendencias, setListaPendencias] = useState([]);

    // Sync on undo/redo
    useEffect(() => { 
        setNomeArquivo(data.nomeArquivo || ''); 
        setFluxogramaData(data.fluxogramaData || null);
        setStatus(data.status || 'aguardando');
    }, [data.nomeArquivo, data.fluxogramaData, data.status]);

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setStatus('carregando');
            
            // Import file
            const result = await importFromGeniaWeb(file);

            if (result.success) {
                // Validate imported data
                const validation = validateImportedData(result.data);

                if (!validation.isValid) {
                    setStatus('erro');
                    alert("Arquivo inválido:\n" + validation.errors.join("\n"));
                    return;
                }

                // Salvar dados do fluxograma
                const novoNome = file.name.replace('.geniaweb', '');
                setNomeArquivo(novoNome);
                setFluxogramaData(result.data);
                setStatus('carregado');

                // Atualizar no node data
                onNodesChange([{ 
                    id, 
                    type: 'update', 
                    data: { 
                        nomeArquivo: novoNome,
                        fluxogramaData: result.data,
                        status: 'carregado'
                    } 
                }]);

            } else {
                setStatus('erro');
                alert("Erro ao importar arquivo: " + result.message);
            }
        } catch (error) {
            setStatus('erro');
            alert("Erro ao processar arquivo: " + error.message);
        }

        // Reset file input
        event.target.value = '';
    };

    const handleRemoveFile = () => {
        setNomeArquivo('');
        setFluxogramaData(null);
        setStatus('aguardando');
        
        onNodesChange([{ 
            id, 
            type: 'update', 
            data: { 
                nomeArquivo: '',
                fluxogramaData: null,
                status: 'aguardando'
            } 
        }]);
    };

    const conferePendencias = () => {
        const pendencias = [];
        if (status === 'aguardando' || !fluxogramaData) {
            pendencias.push('Nenhum fluxograma selecionado');
        }
        if (status === 'erro') {
            pendencias.push('Erro no arquivo selecionado');
        }
        setListaPendencias(pendencias);
        return pendencias;
    };

    useEffect(() => { 
        conferePendencias(); 
    }, [nomeArquivo, fluxogramaData, status]);

    const getStatusIcon = () => {
        switch (status) {
            case 'carregado': return 'fa-solid fa-check-circle';
            case 'erro': return 'fa-solid fa-exclamation-triangle';
            case 'carregando': return 'fa-solid fa-spinner fa-spin';
            default: return 'fa-solid fa-upload';
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'carregado': return '#28a745';
            case 'erro': return '#dc3545';
            case 'carregando': return '#ffc107';
            default: return '#6c757d';
        }
    };

    const getNodesCount = () => {
        if (fluxogramaData && fluxogramaData.nodes) {
            return fluxogramaData.nodes.length;
        }
        return 0;
    };

    return (
        <div className={`node node-fluxograma ${data.highlighted ? 'highlighted' : ''}`}>
            <div className="titulo-node">
                <FontAwesomeIcon icon="fa-solid fa-diagram-project" />
                {data.label.toUpperCase()}
            </div>
            <div className="linha-separadora" />
            <div className="node-geral">
                <div className="node-botoes">
                    <button title="Executar este comando" onClick={() => alert('Executar Fluxograma')}>
                        <FontAwesomeIcon icon="fa-solid fa-circle-play" />
                    </button>
                    <NodeHeaderCustomDeleteAction />
                    {listaPendencias.length > 0 && <NodeHeaderCustomAlertAction conferePendencias={conferePendencias} nodeData={data} />}
                </div>
                
                <div className="node-principal" style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: '12px',
                    minWidth: '280px',
                    maxWidth: '300px',
                    padding: '8px'
                }}>
                    
                    {/* Área de seleção de arquivo */}
                    <div style={{
                        border: '2px dashed #ddd',
                        borderRadius: '8px',
                        padding: '12px',
                        width: '100%',
                        maxWidth: '260px',
                        textAlign: 'center',
                        backgroundColor: status === 'carregado' ? '#f8fff8' : '#fafafa',
                        borderColor: getStatusColor(),
                        cursor: status === 'carregado' ? 'default' : 'pointer',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                    }} onClick={status !== 'carregado' ? handleFileSelect : undefined}>
                        
                        <FontAwesomeIcon 
                            icon={getStatusIcon()} 
                            style={{ 
                                fontSize: '24px', 
                                color: getStatusColor(),
                                marginBottom: '8px'
                            }} 
                        />
                        
                        {status === 'aguardando' && (
                            <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                    Selecionar Fluxograma
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                    Clique para escolher um arquivo .geniaweb
                                </div>
                            </div>
                        )}
                        
                        {status === 'carregando' && (
                            <div style={{ color: '#ffc107' }}>
                                Carregando arquivo...
                            </div>
                        )}
                        
                        {status === 'carregado' && (
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#28a745', marginBottom: '4px', fontSize: '14px' }}>
                                    {nomeArquivo.length > 25 ? nomeArquivo.substring(0, 25) + '...' : nomeArquivo}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '8px' }}>
                                    {getNodesCount()} nós carregados
                                </div>
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFile();
                                    }}
                                    style={{
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#c82333'}
                                    onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-trash" style={{ fontSize: '8px' }} /> 
                                    Remover
                                </button>
                            </div>
                        )}
                        
                        {status === 'erro' && (
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '4px' }}>
                                    Erro no arquivo
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                    Clique para tentar novamente
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Informações adicionais quando carregado */}
                    {status === 'carregado' && fluxogramaData && (
                        <div style={{
                            background: '#e9f7ef',
                            border: '1px solid #c3e6cb',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            width: '100%',
                            maxWidth: '260px',
                            fontSize: '11px',
                            boxSizing: 'border-box'
                        }}>
                            <div><strong>Nós:</strong> {fluxogramaData.nodes?.length || 0}</div>
                            <div><strong>Conexões:</strong> {fluxogramaData.edges?.length || 0}</div>
                            <div><strong>Variáveis:</strong> {fluxogramaData.variables?.length || 0}</div>
                            {fluxogramaData.avatars?.length > 0 && (
                                <div><strong>Avatares:</strong> {fluxogramaData.avatars.length}</div>
                            )}
                        </div>
                    )}

                    <CustomHandle type="target" position="top" connectionCount={1} allowLoopConnections={true} />
                    <CustomHandle type="source" position="bottom" connectionCount={1} />
                </div>
            </div>

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".geniaweb"
                onChange={handleFileImport}
            />
        </div>
    );
};
